import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/useTranslation";
import { ExternalLink } from "lucide-react";
import type { ScriptVersion } from "../types";

interface VersionCardProps {
  version: ScriptVersion;
  isCurrent: boolean;
  formatDate: (date: string) => string;
  onSetCurrent: (versionId: string) => void;
  onEdit: (version: ScriptVersion) => void;
  onArchive: (versionId: string) => void;
  onViewScript: (url?: string | null) => void;
}

export const VersionCard = ({
  version,
  isCurrent,
  formatDate,
  onSetCurrent,
  onEdit,
  onArchive,
  onViewScript,
}: VersionCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">
              {version.version_name
                ? `v${version.version_number} - ${version.version_name}`
                : `Version ${version.version_number}`}
            </h4>
            {isCurrent && (
              <Badge variant="default" className="text-xs">
                {t("versions.current_badge")}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t("versions.created_date", { date: formatDate(version.created_at) })}
          </p>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>
          {t("versions.form_count", { count: version.form_count || 0 })}
        </span>
        <span>
          {t("versions.submission_count", { count: version.submission_count || 0 })}
        </span>
      </div>

      {/* Notes */}
      {version.notes && (
        <div className="text-sm">
          <p className="text-muted-foreground line-clamp-2">
            {version.notes}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewScript(version.script_url)}
          className="gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          {t("versions.view_script")}
        </Button>
        
        {!isCurrent && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSetCurrent(version.id)}
          >
            {t("versions.set_as_current")}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(version)}
        >
          {t("versions.edit")}
        </Button>

        {!isCurrent && (version.form_count || 0) === 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onArchive(version.id)}
            className="text-destructive hover:text-destructive"
          >
            {t("versions.archive")}
          </Button>
        )}
      </div>
    </div>
  );
};
